import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import { RECON_KINDS } from "../../lib/limoGroup";

const TONE = {
  bad: "bg-[#FDECEC] text-[#9b1f2c]",
  warn: "bg-[#FDF3E7] text-[#8a6a1a]",
  info: "bg-[#EDEEF0] text-[#373A45]",
};

/**
 * Our cohorts and members compared with Logiscool (PR L3). The real account
 * is checked every six hours by the sweep; "Verifică acum" runs it at once.
 * Nothing is corrected automatically — where the platform can fix a
 * difference, the button is next to it.
 */
export default function LimoReconciliation() {
  const [sandbox, setSandbox] = useState(false);
  const [report, setReport] = useState(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/limo/reconciliation", { params: { sandbox } });
      setReport(r.data.report);
    } catch {
      setReport(null);
    }
  }, [sandbox]);

  useEffect(() => { setReport(undefined); load(); }, [load]);

  const runNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/limo/reconciliation/run", null, { params: { sandbox } });
      setReport(r.data.report);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Comparația nu a rulat."));
    } finally {
      setBusy(false);
    }
  };

  const fix = async (item, action) => {
    const path = action === "rename" ? "rename" : "sync-trainer";
    const q = action === "rename"
      ? `Trimit la Logiscool numele „${item.group_name}”?`
      : "Pun la Logiscool trainerul ales aici? Cel vechi e scos; dacă Logiscool refuză, e pus la loc.";
    if (!window.confirm(q)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/groups/${item.group_id}/limo/${path}`, null, { params: { sandbox } });
      if (r.data.ok) toast.success(r.data.message); else toast.error(r.data.message);
      await runNow();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Nu a reușit."));
      setBusy(false);
    }
  };

  const items = report?.items || [];
  const byGroup = items.reduce((acc, i) => {
    (acc[i.group_id] = acc[i.group_id] || { name: i.group_name, rows: [] }).rows.push(i);
    return acc;
  }, {});

  return (
    <div className="mt-4 rounded-xl border border-[#E5E7EB] p-3" data-testid="limo-reconciliation">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-[#373A45]">Diferențe față de Logiscool</div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-[11px] text-[#6B7280]">
            <input type="checkbox" checked={sandbox} onChange={(e) => setSandbox(e.target.checked)}
              className="h-3.5 w-3.5 accent-[#965EA7]" /> probă
          </label>
          <button type="button" onClick={runNow} disabled={busy}
            className="inline-flex items-center gap-1 rounded-full border border-[#E5E7EB] bg-white px-3 py-1 text-xs font-semibold hover:bg-[#F5F6F6] disabled:opacity-50"
            data-testid="limo-reconcile-run">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Verifică acum
          </button>
        </div>
      </div>
      <p className="mt-1 text-[11px] text-[#6B7280]">
        {sandbox ? "Grupele de probă se compară doar la cerere." : "Contul real se compară automat la fiecare 6 ore."}
        {report?.at && ` Ultima comparație: ${new Date(report.at).toLocaleString("ro-RO")}.`}
      </p>

      {report === undefined && <p className="mt-2 text-xs text-[#6B7280]">Se încarcă…</p>}
      {report === null && <p className="mt-2 text-xs text-[#6B7280]">Nicio comparație încă. Apasă „Verifică acum”.</p>}
      {report && report.ok === false && (
        <p className="mt-2 text-xs text-[#9b1f2c]">Logiscool nu a putut fi citit ({report.error}). Se reîncearcă automat.</p>
      )}
      {report && report.ok !== false && items.length === 0 && (
        <p className="mt-2 text-xs text-[#2F6B4E]" data-testid="limo-reconcile-clean">
          Totul se potrivește{report.checked_groups ? ` (${report.checked_groups} ${report.checked_groups === 1 ? "grupă" : "grupe"})` : ""}.
        </p>
      )}

      {Object.entries(byGroup).map(([gid, g]) => (
        <div key={gid} className="mt-3">
          <div className="text-xs font-semibold text-[#373A45]">{g.name}</div>
          <ul className="mt-1 space-y-1">
            {g.rows.map((i) => {
              const k = RECON_KINDS[i.kind] || { label: i.kind, tone: "info", hint: () => "" };
              return (
                <li key={`${i.kind}-${i.enrollment_id || ""}-${i.name || ""}`}
                  className={`flex flex-wrap items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-[11px] ${TONE[k.tone]}`}>
                  <span>
                    <strong>{k.label}</strong>{i.name ? ` · ${i.name}` : ""}. {k.hint(i)}
                  </span>
                  {k.action && (
                    <button type="button" onClick={() => fix(i, k.action)} disabled={busy}
                      className="rounded-full bg-white px-2.5 py-0.5 font-semibold text-[#373A45] border border-[#E5E7EB] hover:bg-[#F5F6F6] disabled:opacity-50">
                      {k.action === "rename" ? "Trimite numele" : "Actualizează trainerul"}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
